import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { Subscription } from "rxjs";

import { AuthService } from "src/app/security/auth.service";
import { tieneAccesoMenu } from "src/app/shared/menu/acceso-menu";
import {
  BscAreaDetalle,
  BscEjercicioKpi,
  BscEjercicioTrimestre,
  BscRes,
  CorteBsc,
  EstatusTrimestreBsc,
  SemaforoBsc,
} from "src/app/shared/model/bsc/bsc.model";
import { BscService } from "../bsc.service";
import { FraccionesBarra, PistaBarra, enPista, escalaBarras } from "../bsc-calculos";
import { guardarDescarga, mensajeDeDescarga } from "../bsc-descarga";
import {
  ACCION_HISTORIAL,
  ENTREGA,
  ESTATUS_PLANEACION,
  ESTATUS_TRIMESTRE,
  EstadoBsc,
  SEMAFORO,
  textoSinAnterior,
} from "../bsc-estados";
import { SIN_DATO, fechaCorta, meta, nombres, numero, pct, pts } from "../bsc-formato";
import { acumProgEn, acumRealEn, aplicaEn, semaforoEn } from "../bsc-kpi";
import { AcumuladoQBscComponent } from "../graficas/acumulado-q-bsc.component";
import { SelectorCorteBscComponent, SeleccionCorteBsc } from "../selector-corte/selector-corte-bsc.component";
import { TileBsc, TilesBscComponent, avance, claseBarra, claseNota, claseTile } from "../tiles/tiles-bsc.component";

/**
 * Qué se puede enseñar del área en el año al corte:
 *   DATOS          tiles, gráfica, trimestres y la tabla de KPIs con su acumulado;
 *   SIN_LECTURA    no se pudo consultar el ciclo o las planeaciones: no se afirma que no
 *                  existan (ni se ofrece crearlos o cargarlos), solo los avisos;
 *   SIN_CICLO      el año no está dado de alta: sin tabla;
 *   SIN_PLANEACION el área no tiene planeación en el año: sin tabla;
 *   NO_AUTORIZADA  la planeación existe pero no está autorizada: sus KPIs sin avance;
 *   SIN_CORTE      corte Anual sin ningún trimestre autorizado del área: la tabla sin valores.
 */
type EstadoDetalle = "DATOS" | "SIN_LECTURA" | "SIN_CICLO" | "SIN_PLANEACION" | "NO_AUTORIZADA" | "SIN_CORTE";

/** Una celda de trimestre de la tabla: el acumulado, "NA" o "—". */
interface CeldaQ {
  texto: string;
  /** is-na: el KPI no aplica; is-vacio: sin dato o después del corte. */
  clase: "" | "is-na" | "is-vacio";
}

/**
 * La barra de "Acumulado": real al corte sobre el año (fracciones) con la raya
 * de lo programado; `pista`, dónde se pinta con la escala de la tabla.
 */
interface BarraAcumulado extends FraccionesBarra {
  pista: PistaBarra;
  clase: string;
  texto: string;
  /** Lo que lee un lector de pantalla en vez de la barra. */
  accesible: string;
}

/** Un renglón de la tabla de KPIs, armado UNA vez por respuesta (como las filas de A1). */
interface FilaKpi {
  sugerido?: boolean;
  idEjercicioKpi: number | null;
  objetivo: string;
  indicador: string;
  /** Objetivo e indicador sin acentos ni mayúsculas, para el buscador. */
  busqueda: string;
  meta: string;
  celdas: CeldaQ[];
  barra: BarraAcumulado | null;
  estado: EstadoBsc<SemaforoBsc> | null;
  retirado: boolean;
  etiquetaFicha: string;
}

/** Un trimestre del área en la tarjeta "Trimestres". */
interface FilaTrimestre {
  q: number;
  estatus: EstadoBsc<EstatusTrimestreBsc> | null;
  /** Debajo del estatus: vencido, sin datos o la entrega del corte. */
  nota: string;
  notaClase: "" | "is-peligro" | "is-neutro";
  alcance: string;
  programado: string;
  /** El alcance es el snapshot congelado al autorizar. */
  congelado: boolean;
  semaforo: EstadoBsc<SemaforoBsc> | null;
  movimientos: string[];
  limite: string;
  esCorte: boolean;
}

/** Los cortes que acepta area/detalle.do, para leer el de la URL. */
const CORTES = new Set<CorteBsc>(["1", "2", "3", "4", "ANUAL"]);
const TRIMESTRES = [1, 2, 3, 4];
/** Los estatus del trimestre que esperan el envío del área (pueden vencer, §4). */
const ABIERTOS = new Set<string>(["PENDIENTE", "EN_CAPTURA", "REGRESADO"]);

/** Un valor de los queryParams que llevan a las otras pantallas. */
type ValorQuery = string | number | null;

/** Rutas de las otras pantallas del módulo. */
const RUTA = {
  tablero: "/admin/bsc/tablero",
  kpi: "/admin/bsc/tablero/kpi",
  cargaExcel: "/admin/bsc/carga-excel",
  comparativo: "/admin/bsc/comparativo",
  revision: "/admin/bsc/revision",
  parametros: "/admin/bsc/parametros",
  planeacion: "/admin/bsc/planeacion",
  cierre: "/admin/bsc/mi-bsc/cierre",
};

/**
 * BSC · A2 Detalle de área (docs/BSC.md §7 y Anexo B, maqueta
 * «A2-DetalleArea»): un área en el año al corte. Tiles (alcance del corte
 * con su semáforo, KPIs en meta con la regla del ciclo, contra el año
 * anterior al mismo corte y la entrega del trimestre), el acumulado
 * programado contra real por trimestre, los 4 trimestres con su estatus y
 * quién envió y resolvió, y la tabla de KPIs con su acumulado por trimestre,
 * su estado al corte y el enlace a la ficha (A3).
 *
 * Solo lectura (F4): basta la hoja raíz admin/bsc y el área de la empresa
 * (area/detalle.do, BscConsultaBuss); un Responsable abre cualquier área.
 * Exportar FOR-011 (F4.4, exportar.do) baja el libro de la planeación al
 * corte elegido, con la misma regla de acceso. «Cerrar Qn» (F6.5; «Enviar Qn
 * a revisión» si el área captura con Excel) lleva a C3 con ?desde=area
 * cuando puedeCerrar y el trimestre abierto del área está en captura o
 * regresado; Capturar mes no está (se captura en Mi BSC y en la ficha).
 *
 * Base visual: la de A1, de la que es el detalle: hero, selector y tabla de
 * table-page, tiles de app-tiles-bsc y tarjetas .tickets-card de
 * dashboard-tarjetas.css dentro de un .tickets-dashboard (los colores de
 * oscuro y cristal cuelgan de esa clase). Año y corte viajan en la URL
 * (?anio=&corte=), así "Tablero BSC" regresa al mismo corte.
 */
@Component({
  selector: "app-detalle-area-bsc",
  standalone: true,
  templateUrl: "./detalle-area-bsc.component.html",
  styleUrls: [
    "../../dashboard/shared/dashboard-tarjetas.css",
    "../bsc.shared.css",
    "./detalle-area-bsc.component.css",
  ],
  imports: [
    CommonModule,
    RouterLink,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    SelectorCorteBscComponent,
    TilesBscComponent,
    AcumuladoQBscComponent,
  ],
})
export class DetalleAreaBscComponent implements OnInit, OnDestroy {
  readonly ruta = RUTA;
  readonly trimestres = TRIMESTRES;

  idBscArea: number | null = null;
  anioInicial: number | null = null;
  corteInicial: CorteBsc | null = null;

  anio: number | null = null;
  corte: CorteBsc | null = null;
  loading = false;
  error = "";

  detalle: BscAreaDetalle | null = null;
  estado: EstadoDetalle = "DATOS";
  /** El trimestre del corte (en Anual, el último autorizado del área). */
  q: number | null = null;
  nombreArea = "";
  tiles: TileBsc[] = [];
  filasTrimestre: FilaTrimestre[] = [];
  filas: FilaKpi[] = [];
  filasVisibles: FilaKpi[] = [];
  filtro = "";
  /** El pie de la tabla: el alcance del área por trimestre. */
  pie: CeldaQ[] = [];
  pieBarra: BarraAcumulado | null = null;
  pieSemaforo: EstadoBsc<SemaforoBsc> | null = null;
  /** Trimestres cuyo alcance del área es el snapshot (para la nota del pie). */
  congelados: number[] = [];
  avisos: string[] = [];

  puedeCargarExcel = false;
  puedeConfigurar = false;
  puedeSugerir = false;
  cargandoSugerencias = false;
  sugerenciaPendiente: number | null = null;
  mensajeSugerencia = "";
  private sugerencias?: Subscription;
  private escrituraSugerencia?: Subscription;
  puedePlanear = false;
  puedeComparar = false;
  puedeRevisar = false;
  /** Exportar FOR-011: el área tiene planeación en el año (basta la hoja raíz). */
  puedeExportar = false;
  exportando = false;
  /** Lo que impidió bajar el FOR-011 (sin permiso, sin conexión). */
  errorExportar = "";
  /**
   * F6.5: el trimestre que se ofrece cerrar (C3), el primero EN_CAPTURA o
   * REGRESADO de la planeación autorizada; nulo sin puedeCerrar, en un
   * histórico o con el año cerrado (C3 vuelve a revisar todo con la previa).
   */
  qCierre: number | null = null;
  textoCierre = "";

  private consulta?: Subscription;
  private ruta$?: Subscription;
  private descarga?: Subscription;

  constructor(
    private readonly bscService: BscService,
    private readonly authService: AuthService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get("idBscArea"));
    this.idBscArea = Number.isInteger(id) && id > 0 ? id : null;
    // Angular reutiliza el componente si solo cambia el área de la ruta (la barra
    // de direcciones o el historial del navegador): se vuelve a consultar con el
    // mismo año y corte. La primera consulta la dispara el selector al cargar.
    this.ruta$ = this.route.paramMap.subscribe((ruta) => {
      const otra = Number(ruta.get("idBscArea"));
      const nueva = Number.isInteger(otra) && otra > 0 ? otra : null;
      if (nueva !== this.idBscArea && nueva != null) {
        this.idBscArea = nueva;
        this.filtro = "";
        this.nombreArea = "";
        this.detalle = null;
        this.puedeExportar = false;
        this.errorExportar = "";
        this.consultar();
      }
    });
    const params = this.route.snapshot.queryParamMap;
    const anio = Number(params.get("anio"));
    this.anioInicial = Number.isInteger(anio) && anio > 1900 && anio < 3000 ? anio : null;
    const corte = (params.get("corte") ?? "").toUpperCase() as CorteBsc;
    this.corteInicial = CORTES.has(corte) ? corte : null;

    // T2 y C2 no traen bandera puede*: se ofrecen si el perfil tiene su hoja.
    const menus = this.authService.getMenuList();
    this.puedeComparar = tieneAccesoMenu(menus, RUTA.comparativo);
    this.puedeRevisar = tieneAccesoMenu(menus, RUTA.revision);
    if (this.idBscArea == null) {
      this.error = "El enlace no trae un área válida. Ábrela desde el tablero.";
    }
  }

  ngOnDestroy(): void {
    this.consulta?.unsubscribe();
    this.ruta$?.unsubscribe();
    this.sugerencias?.unsubscribe();
    this.escrituraSugerencia?.unsubscribe();
    this.descarga?.unsubscribe();
  }

  /* ============================ Consulta ============================ */

  onSeleccion(seleccion: SeleccionCorteBsc): void {
    this.anio = seleccion.anio;
    this.corte = seleccion.corte ?? "ANUAL";
    this.errorExportar = "";
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { anio: this.anio, corte: this.corte },
      replaceUrl: true,
    });
    this.consultar();
  }

  consultar(): void {
    if (this.anio == null || this.corte == null || this.idBscArea == null) {
      return;
    }
    const pedido = `${this.idBscArea}|${this.anio}|${this.corte}`;
    this.consulta?.unsubscribe();
    this.loading = true;
    this.error = "";
    this.cdr.detectChanges();

    this.consulta = this.bscService.areaDetalle(this.anio, this.idBscArea, this.corte).subscribe({
      next: (res) => {
        if (pedido !== `${this.idBscArea}|${this.anio}|${this.corte}`) {
          return;
        }
        this.loading = false;
        if (res?.codigoError) {
          this.error = res.codigoError === "AUTH-001"
            ? "Tu sesión caducó. Vuelve a entrar."
            : res.mensajeError || "No se pudo consultar el detalle del área.";
          this.detalle = null;
          this.puedeExportar = false;
          this.cdr.detectChanges();
          return;
        }
        this.aplica(res);
        this.cdr.detectChanges();
      },
      error: () => {
        if (pedido !== `${this.idBscArea}|${this.anio}|${this.corte}`) {
          return;
        }
        this.loading = false;
        this.error = "No se pudo consultar el detalle del área: sin conexión con el servidor.";
        this.detalle = null;
        this.puedeExportar = false;
        this.cdr.detectChanges();
      },
    });
  }

  private aplica(res: BscRes): void {
    const d: BscAreaDetalle = res.areaDetalle ?? {};
    this.detalle = d;
    this.puedeSugerir = res.puedeCerrar === true || res.puedeCapturar === true;
    this.puedeCargarExcel = res.puedeCargarExcel === true;
    this.puedeConfigurar = res.puedeConfigurar === true;
    this.puedePlanear = res.puedePlanear === true;
    this.q = d.trimestreCorte ?? null;
    this.avisos = d.avisos ?? [];
    this.nombreArea = d.area?.nombre || `Área ${this.idBscArea ?? ""}`.trim();

    const autorizada = d.ejercicio?.estatus === "AUTORIZADA";
    this.estado = this.estadoDe(d, autorizada);

    const real = d.serieRealQ ?? [];
    this.congelados = TRIMESTRES.filter((q) => {
      const t = this.trimestre(d, q);
      return t?.estatus === "AUTORIZADO" && numero(t.alcanceRealSnap) != null && numero(real[q - 1]) != null;
    });
    this.filas = (d.kpis ?? []).map((k) => this.fila(k, autorizada));
    this.pie = autorizada ? TRIMESTRES.map((q) => celda(real[q - 1])) : [];
    this.pieBarra = autorizada && this.q != null
      ? barra(d.alcanceCorte, d.programadoCorte, d.semaforo, `Alcance del área ${this.textoCorte}`)
      : null;
    // Después del pie: su barra entra a la escala de la tabla.
    this.aplicaFiltro(this.filtro);
    this.pieSemaforo = this.semaforo(d.semaforo);
    this.filasTrimestre = this.estado === "DATOS" ? TRIMESTRES.map((q) => this.filaTrimestre(d, q)) : [];
    this.tiles = this.estado === "DATOS" ? this.armaTiles(d) : [];
    // Una planeación sin autorizar también se exporta: sale su planeación, sin avances.
    this.puedeExportar = d.ejercicio?.idEjercicio != null;
    this.ponCierre(res, d, autorizada);
    this.leerSugerencias();
  }

  private leerSugerencias(): void {
    this.sugerencias?.unsubscribe();
    this.mensajeSugerencia = "";
    if (this.anio == null || this.corte == null) { return; }
    this.cargandoSugerencias = true;
    this.sugerencias = this.bscService.indicadoresConsultar(this.anio, this.corte).subscribe({
      next: (res) => {
        this.cargandoSugerencias = false;
        if (res.codigoError) {
          this.mensajeSugerencia = res.mensajeError ?? "No se pudieron leer las sugerencias.";
        } else {
          const sugerencias = new Set(res.sugerencias ?? []);
          for (const f of this.filas) {
            f.sugerido = sugerencias.has(f.idEjercicioKpi);
          }
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.cargandoSugerencias = false;
        this.mensajeSugerencia = "No se pudieron leer las sugerencias. Revisa tu conexión.";
        this.cdr.markForCheck();
      },
    });
  }

  sugerir(f: FilaKpi): void {
    if (!this.puedeSugerir || this.cargandoSugerencias || this.sugerenciaPendiente != null || f.idEjercicioKpi == null) { return; }
    const sugerido = f.sugerido !== true;
    this.sugerenciaPendiente = f.idEjercicioKpi;
    this.mensajeSugerencia = "";
    this.escrituraSugerencia = this.bscService.indicadoresSugerir({ idEjercicioKpi: f.idEjercicioKpi, sugerido }).subscribe({
      next: (res) => {
        this.sugerenciaPendiente = null;
        if (res.codigoError) {
          this.mensajeSugerencia = res.mensajeError ?? "No se pudo guardar la sugerencia.";
        } else {
          f.sugerido = sugerido;
          this.mensajeSugerencia = sugerido ? "Sugerencia guardada." : "Sugerencia retirada.";
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.sugerenciaPendiente = null;
        this.mensajeSugerencia = "No se pudo confirmar la sugerencia. Revisa tu conexión e intenta de nuevo.";
        this.cdr.markForCheck();
      },
    });
  }

  /** Qué se puede enseñar del área en el año al corte (ver EstadoDetalle). */
  private estadoDe(d: BscAreaDetalle, autorizada: boolean): EstadoDetalle {
    if (d.ejercicio == null) {
      // Un bloque que falló no es un "no existe": cicloLeido/planeacionesLeidas lo distinguen.
      const sinLeer = d.cicloLeido === false || (d.estatusCiclo != null && d.planeacionesLeidas === false);
      if (sinLeer) {
        return "SIN_LECTURA";
      }
      return d.estatusCiclo == null ? "SIN_CICLO" : "SIN_PLANEACION";
    }
    if (!autorizada) {
      return "NO_AUTORIZADA";
    }
    return this.q == null ? "SIN_CORTE" : "DATOS";
  }

  /**
   * «Cerrar Qn» a C3: las dos vías terminan en el mismo cierre (§1.1); con
   * Excel el área no captura ahí, solo envía lo que cargó.
   */
  private ponCierre(res: BscRes, d: BscAreaDetalle, autorizada: boolean): void {
    const abierto = (d.trimestres ?? [])
      .filter((t) => t.estatus === "EN_CAPTURA" || t.estatus === "REGRESADO")
      .sort((a, b) => (a.trimestre ?? 0) - (b.trimestre ?? 0))[0];
    this.qCierre =
      res.puedeCerrar === true &&
      autorizada &&
      d.ejercicio?.idEjercicio != null &&
      d.ejercicio.origen !== "IMPORTACION_HISTORICA" &&
      d.estatusCiclo !== "CERRADO" &&
      abierto?.trimestre != null
        ? abierto.trimestre
        : null;
    if (this.qCierre == null) {
      this.textoCierre = "";
    } else {
      this.textoCierre = d.area?.viaCaptura === "EXCEL" ? `Enviar Q${this.qCierre} a revisión` : `Cerrar Q${this.qCierre}`;
    }
  }

  /* ============================ Exportar ============================ */

  /** Baja el FOR-011 de la planeación del área al corte que se está viendo. */
  exportar(): void {
    const idEjercicio = this.detalle?.ejercicio?.idEjercicio;
    if (idEjercicio == null || this.exportando) {
      return;
    }
    const sufijoQ = this.q != null ? ` Q${this.q}` : "";
    const porOmision = `FOR-011 BSC ${this.nombreArea} ${this.anio ?? ""}${sufijoQ}`;
    this.exportando = true;
    this.errorExportar = "";
    this.cdr.detectChanges();
    this.descarga?.unsubscribe();
    this.descarga = this.bscService.exportar(idEjercicio, this.corte).subscribe({
      next: (res) => {
        this.exportando = false;
        guardarDescarga(res, `${porOmision.trim()}.xlsx`);
        this.cdr.detectChanges();
      },
      error: (err: unknown) => {
        void mensajeDeDescarga(err, "exportar el FOR-011").then((mensaje) => {
          this.exportando = false;
          this.errorExportar = mensaje;
          this.cdr.detectChanges();
        });
      },
    });
  }

  /* ============================ Encabezado ============================ */

  get titulo(): string {
    if (!this.nombreArea) {
      return "Detalle de área";
    }
    return this.anio != null ? `${this.nombreArea} · ${this.anio}` : this.nombreArea;
  }

  /** "8 KPIs · responsable: Ana López · último movimiento: Trimestre enviado a revisión Q2, 3 jul 2026". */
  get resumen(): string {
    const d = this.detalle;
    if (!d || this.loading || this.error) {
      return "KPIs del área con su acumulado por trimestre, el semáforo, la entrega y el año anterior al mismo corte.";
    }
    const partes: string[] = [];
    if (d.ejercicio) {
      const activos = (d.kpis ?? []).filter((k) => k.estatus !== "RETIRADO").length;
      partes.push(`${activos} ${activos === 1 ? "KPI" : "KPIs"}`);
    }
    const responsables = (d.area?.usuarios ?? [])
      .filter((u) => u.rol === "RESPONSABLE" && u.vigente !== false)
      .map((u) => u.usuario || SIN_DATO);
    const quienes = responsables.length === 1 ? "responsable" : "responsables";
    partes.push(responsables.length > 0
      ? `${quienes}: ${nombres(responsables)}`
      : "sin responsable asignado");
    const m = d.ultimoMovimiento;
    if (m?.accion) {
      const accion = ACCION_HISTORIAL[m.accion] ?? m.accion;
      const q = m.trimestre ? ` Q${m.trimestre}` : "";
      partes.push(`último movimiento: ${accion.toLowerCase()}${q}, ${fechaCorta(m.fecha)}`);
    }
    return partes.join(" · ");
  }

  /** "al corte Q2" o "al corte anual (Q4)". */
  get textoCorte(): string {
    if (this.q == null) {
      return this.corte === "ANUAL" ? "al corte anual" : "";
    }
    return this.corte === "ANUAL" ? `al corte anual (Q${this.q})` : `al corte Q${this.q}`;
  }

  /** La planeación del área, a la derecha del selector. */
  get textoPlaneacion(): string {
    const d = this.detalle;
    if (!d) {
      return "";
    }
    if (this.estado === "SIN_LECTURA") {
      return "No se pudo leer el año";
    }
    if (d.estatusCiclo == null) {
      return "Sin ciclo";
    }
    const e = d.ejercicio;
    if (!e) {
      return "Sin planeación";
    }
    if (e.estatus !== "AUTORIZADA") {
      const estatus = ESTATUS_PLANEACION.find((x) => x.valor === e.estatus);
      return `Planeación ${estatus ? estatus.texto.toLowerCase() : "sin autorizar"}`;
    }
    const cerrado = d.estatusCiclo === "CERRADO" ? " · año cerrado" : "";
    if (e.origen === "IMPORTACION_HISTORICA") {
      return `Histórico importado${cerrado}`;
    }
    if (e.origen === "IMPORTACION_ARRANQUE") {
      return `Planeación autorizada · arranque importado${cerrado}`;
    }
    return `Planeación autorizada${cerrado}`;
  }

  get textoCorteMeta(): string {
    if (!this.detalle || this.loading) {
      return this.corte === "ANUAL" ? "Anual" : `Corte Q${this.corte}`;
    }
    if (this.q != null) {
      return this.corte === "ANUAL" ? `Anual · hasta Q${this.q}` : `Corte Q${this.q}`;
    }
    if (this.corte !== "ANUAL") {
      return `Corte Q${this.corte}`;
    }
    return this.estado === "SIN_CORTE" ? "Anual · sin trimestres autorizados" : "Anual";
  }

  /** La tabla vacía porque los KPI no se pudieron leer, no porque la planeación no tenga. */
  get kpisSinLeer(): boolean {
    return this.detalle?.kpisLeidos === false;
  }

  /** "en borrador", "enviada a autorización" o "regresada". */
  get textoEstatusPlaneacion(): string {
    const estatus = ESTATUS_PLANEACION.find((x) => x.valor === this.detalle?.ejercicio?.estatus);
    if (!estatus) {
      return "sin autorizar";
    }
    return estatus.valor === "BORRADOR" ? "en borrador" : estatus.texto.toLowerCase();
  }

  /** Los parámetros de vuelta al tablero (mismo año y corte). */
  get queryTablero(): Record<string, ValorQuery> {
    return { anio: this.anio, corte: this.corte };
  }

  /** Planeación (T3) abre esta área en este año. */
  get queryPlaneacion(): Record<string, number | null> {
    return { anio: this.anio, idBscArea: this.idBscArea };
  }

  /** T2 (F7.1) del área: este año contra el anterior, al mismo corte (Anual = cierre anual). */
  get queryComparativo(): Record<string, ValorQuery> {
    const anual = this.corte === "ANUAL";
    return {
      anioA: this.anio != null ? this.anio - 1 : null,
      anioB: this.anio,
      idBscArea: this.idBscArea,
      modo: anual ? "cierre" : "corte",
      q: anual ? null : this.corte,
    };
  }

  /**
   * T2 desde el tile «Contra <año>» (F7.3): al mismo trimestre que compara el
   * tile, también con el corte Anual (su trimestre es el último autorizado).
   */
  private queryComparativoTile(q: number): Record<string, ValorQuery> {
    return { ...this.queryComparativo, modo: "corte", q };
  }

  /* ============================ Tiles ============================ */

  private armaTiles(d: BscAreaDetalle): TileBsc[] {
    const q = this.q as number;
    return [this.tileAlcance(d, q), this.tileKpis(d), this.tileAnterior(d, q), this.tileEntrega(d, q)];
  }

  private tileAlcance(d: BscAreaDetalle, q: number): TileBsc {
    const alcance = numero(d.alcanceCorte);
    const programado = numero(d.programadoCorte);
    const semaforo = this.semaforo(d.semaforo);
    return {
      etiqueta: this.corte === "ANUAL" ? `Alcance anual (Q${q})` : `Alcance Q${q}`,
      valor: pct(alcance),
      meta: `de ${pct(programado)} programado${this.congelados.includes(q) ? " · congelado al autorizar" : ""}`,
      icono: "insights",
      clase: claseTile(d.semaforo),
      nota: semaforo ? semaforo.texto : undefined,
      notaClase: claseNota(d.semaforo),
      barra: avance(alcance, programado),
      barraClase: claseBarra(d.semaforo),
    };
  }

  private tileKpis(d: BscAreaDetalle): TileBsc {
    const tile: TileBsc = {
      etiqueta: "KPIs en meta",
      valor: SIN_DATO,
      // Un trimestre después de lo cargado de un histórico no tiene avances que contar.
      meta: d.estadoEntrega === "SIN_DATOS"
        ? "Sin datos: el histórico se cargó hasta un trimestre anterior"
        : "No se pudieron calcular los KPIs al corte",
      icono: "traffic",
      clase: "ticket-kpi--muted",
    };
    if (d.kpisEnMeta == null) {
      return tile;
    }
    const enRiesgo = d.kpisEnRiesgo ?? 0;
    const fuera = d.kpisFuera ?? 0;
    const porIniciar = d.kpisPorIniciar ?? 0;
    const vigentes = d.kpisVigentes ?? d.kpisEnMeta + enRiesgo + fuera;
    tile.valor = `${d.kpisEnMeta} / ${vigentes}`;
    const partes = [`${enRiesgo} en riesgo`, `${fuera} fuera`];
    if (porIniciar > 0) {
      partes.push(`${porIniciar} ${porIniciar === 1 ? "inicia" : "inician"} después`);
    }
    tile.meta = partes.join(" · ");
    tile.clase = claseConteos(fuera, enRiesgo);
    if (d.semaforoVerde != null) {
      tile.nota = `En meta desde ${pct(d.semaforoVerde)} de lo programado` +
        (d.semaforoAmarillo != null ? `; en riesgo desde ${pct(d.semaforoAmarillo)}` : "");
    }
    return tile;
  }

  /**
   * «Contra <año anterior>» (maqueta A2, F7.3): el alcance del área el año
   * anterior al mismo trimestre y la diferencia en puntos, los dos del
   * servidor (alcanceAnteriorMismoCorte, delta); sin ellos, por qué
   * (estadoAnterior). «Ver comparativo completo» abre T2 en ese trimestre si
   * el año anterior tiene planeación del área.
   */
  private tileAnterior(d: BscAreaDetalle, q: number): TileBsc {
    const anio = d.anio ?? this.anio ?? 0;
    const anterior = anio - 1;
    const alcance = numero(d.alcanceAnteriorMismoCorte);
    const delta = numero(d.delta);
    const tile: TileBsc = {
      etiqueta: `Contra ${anterior}`,
      valor: pts(delta),
      meta: alcance != null
        ? `Al mismo corte (Q${q}), ${anterior} iba en ${pct(alcance)}`
        : textoSinAnterior(d.estadoAnterior, anterior, q, d.datosHastaAnterior),
      icono: "compare_arrows",
      clase: claseDiferencia(delta),
    };
    if (delta != null) {
      // Un trimestre sin autorizar se compara en vivo: puede cambiar (T2 avisa lo mismo).
      const vivo = this.congelados.includes(q) ? "" : " · en vivo";
      tile.nota = notaDiferencia(delta) + vivo;
      tile.notaClase = tonoDiferencia(delta);
    } else if (alcance != null) {
      // Hay con qué comparar, pero este año no tiene alcance en ese trimestre (un histórico cargado antes).
      tile.nota = `Sin alcance de ${anio} al Q${q}`;
    }
    // Sin año anterior que leer, T2 no tendría qué enseñar (queda «Comparar años» del encabezado).
    const sinAnterior = ["SIN_LECTURA", "SIN_CICLO", "SIN_PLANEACION"].includes(d.estadoAnterior ?? "");
    if (this.puedeComparar && !sinAnterior) {
      tile.enlace = { texto: "Ver comparativo completo", ruta: RUTA.comparativo, queryParams: this.queryComparativoTile(q) };
    }
    return tile;
  }

  private tileEntrega(d: BscAreaDetalle, q: number): TileBsc {
    const entrega = ENTREGA.find((e) => e.valor === d.estadoEntrega) ?? null;
    const t = this.trimestre(d, q);
    const tile: TileBsc = {
      etiqueta: `Entrega Q${q}`,
      valor: entrega ? textoCortoEntrega(entrega.valor) : SIN_DATO,
      meta: "",
      icono: "assignment_turned_in",
      clase: "ticket-kpi--muted",
      compacto: true,
    };
    if (!entrega || !t) {
      tile.meta = "No se pudo leer el estatus del trimestre";
      return tile;
    }
    this.ponEntrega(tile, entrega.valor, t, q);
    const sinCaptura = d.kpisSinCaptura ?? 0;
    if (sinCaptura > 0) {
      tile.nota = `${sinCaptura} ${sinCaptura === 1 ? "KPI sin avance capturado" : "KPIs sin avance capturado"}`;
      tile.notaClase = tile.notaClase ?? "is-aviso";
    } else {
      tile.notaClase = undefined;
    }
    return tile;
  }

  /** El color, el texto y el enlace del tile «Entrega Qn» según la entrega del trimestre. */
  private ponEntrega(tile: TileBsc, entrega: string, t: BscEjercicioTrimestre, q: number): void {
    const limite = t.fechaLimiteEnvio ? `límite ${fechaCorta(t.fechaLimiteEnvio)}` : "sin fecha límite";
    switch (entrega) {
      case "AUTORIZADA":
        tile.clase = "ticket-kpi--done";
        tile.meta = t.fechaResolucion ? `Autorizada el ${fechaCorta(t.fechaResolucion)}` : "Autorizada";
        if (t.origen === "IMPORTACION") {
          tile.meta = "Autorizada al cargar el FOR-011";
        }
        break;
      case "ENVIADA":
        tile.clase = "ticket-kpi--active";
        tile.meta = t.fechaEnvio ? `Enviada el ${fechaCorta(t.fechaEnvio)}` : "Enviada a revisión";
        if (this.puedeRevisar) {
          // C2 (F6.6) abre en el año y el trimestre, con el envío del área a un lado.
          tile.enlace = {
            texto: "Abrir la revisión",
            ruta: RUTA.revision,
            queryParams: { anio: this.anio, trimestre: q, id: t.id ?? null },
          };
        }
        break;
      case "VENCIDA":
        tile.clase = "ticket-kpi--total";
        tile.meta = `Sin enviar · ${limite}`;
        tile.notaClase = "is-peligro";
        break;
      case "PENDIENTE": {
        tile.clase = "ticket-kpi--time";
        const estatus = ESTATUS_TRIMESTRE.find((e) => e.valor === t.estatus);
        const delTrimestre = estatus ? `Trimestre ${estatus.texto.toLowerCase()}` : "Pendiente";
        tile.meta = `${delTrimestre} · ${limite}`;
        tile.notaClase = "is-aviso";
        break;
      }
      default:
        tile.meta = "Histórico cargado hasta un trimestre anterior";
    }
  }

  /* ============================ Trimestres ============================ */

  private filaTrimestre(d: BscAreaDetalle, q: number): FilaTrimestre {
    const t = this.trimestre(d, q);
    const despues = this.q != null && q > this.q;
    const real = despues ? null : numero(d.serieRealQ?.[q - 1]);
    const fila: FilaTrimestre = {
      q,
      estatus: ESTATUS_TRIMESTRE.find((e) => e.valor === t?.estatus) ?? null,
      nota: "",
      notaClase: "",
      alcance: pct(real),
      programado: pct(d.serieProgramadoQ?.[q - 1]),
      congelado: this.congelados.includes(q),
      semaforo: real != null ? this.semaforo(t?.semaforo) : null,
      movimientos: [],
      // La fecha límite solo dice algo mientras el trimestre espera el envío.
      limite: textoLimite(t),
      esCorte: q === this.q,
    };
    if (!t) {
      fila.nota = "Sin renglón del flujo";
      fila.notaClase = "is-neutro";
      return fila;
    }
    if (t.sinEnviar) {
      fila.nota = "Vencido: la fecha límite ya pasó";
      fila.notaClase = "is-peligro";
    } else if (t.estatus === "PENDIENTE" && d.ejercicio?.origen === "IMPORTACION_HISTORICA") {
      fila.nota = "Sin datos: el histórico se cargó hasta un trimestre anterior";
      fila.notaClase = "is-neutro";
      fila.limite = "";
    }
    fila.movimientos = movimientos(t);
    return fila;
  }

  private trimestre(d: BscAreaDetalle, q: number): BscEjercicioTrimestre | undefined {
    return (d.trimestres ?? []).find((t) => t.trimestre === q);
  }

  /* ============================ KPIs ============================ */

  private fila(k: BscEjercicioKpi, autorizada: boolean): FilaKpi {
    const objetivo = k.objetivo || `KPI ${k.idEjercicioKpi ?? ""}`.trim();
    const q = this.q;
    const retirado = k.estatus === "RETIRADO";
    const celdas = TRIMESTRES.map((i): CeldaQ => {
      if (!aplicaEn(k, i)) {
        return { texto: "NA", clase: "is-na" };
      }
      return celda(autorizada ? acumRealEn(k, i) : null);
    });
    let estado: EstadoBsc<SemaforoBsc> | null = null;
    let barraKpi: BarraAcumulado | null = null;
    if (autorizada && q != null) {
      // Un KPI retirado ya no se evalúa (no entra al promedio en vivo): su acumulado sin semáforo.
      const semaforo = retirado ? null : semaforoEn(k, q);
      estado = this.semaforo(semaforo);
      if (!semaforo?.startsWith("INICIA_Q")) {
        barraKpi = barra(acumRealEn(k, q), acumProgEn(k, q), semaforo, `Acumulado de ${objetivo} ${this.textoCorte}`);
      }
    } else if (!autorizada && (k.trimestreInicio ?? 1) > 1) {
      estado = this.semaforo(`INICIA_Q${k.trimestreInicio}` as SemaforoBsc);
    }
    return {
      idEjercicioKpi: k.idEjercicioKpi ?? null,
      objetivo,
      indicador: k.indicador || "",
      busqueda: normaliza(`${objetivo} ${k.indicador ?? ""}`),
      meta: meta(k.metaValor, k.metaTexto, k.unidad),
      celdas,
      barra: barraKpi,
      estado,
      retirado,
      etiquetaFicha: `Ficha de ${objetivo}`,
    };
  }

  onFiltro(texto: string): void {
    this.filtro = texto;
    this.aplicaFiltro(texto);
  }

  private aplicaFiltro(texto: string): void {
    const buscado = normaliza(texto).trim();
    this.filasVisibles = buscado ? this.filas.filter((f) => f.busqueda.includes(buscado)) : this.filas;
    this.ponEscala();
  }

  /**
   * Una escala común a las barras que se ven (escalaBarras): las filas que
   * deja el buscador y, sin filtro, la del pie. Con un KPI arriba de 100 %
   * las demás se acortan en proporción en vez de topar todas en 100 %.
   */
  private ponEscala(): void {
    const barras = [...this.filasVisibles.map((f) => f.barra), this.filtro ? null : this.pieBarra]
      .filter((b): b is BarraAcumulado => b != null);
    const escala = escalaBarras(barras);
    for (const b of barras) {
      b.pista = enPista(b, escala);
    }
  }

  private semaforo(valor: SemaforoBsc | null | undefined): EstadoBsc<SemaforoBsc> | null {
    return SEMAFORO.find((s) => s.valor === valor) ?? null;
  }

  trackFila(_: number, f: FilaKpi): string {
    return `${f.idEjercicioKpi ?? f.objetivo}`;
  }

  trackTrimestre(_: number, t: FilaTrimestre): number {
    return t.q;
  }
}

/** Una celda de acumulado: el porcentaje o "—". */
function celda(valor: number | null | undefined): CeldaQ {
  const n = numero(valor);
  return n == null ? { texto: SIN_DATO, clase: "is-vacio" } : { texto: pct(n), clase: "" };
}

/** La barra de acumulado, en la escala 1 hasta que ponEscala le pone la de la tabla; sin real no hay barra. */
function barra(
  real: number | null | undefined,
  programado: number | null | undefined,
  semaforo: SemaforoBsc | null | undefined,
  que: string,
): BarraAcumulado | null {
  const r = numero(real);
  if (r == null) {
    return null;
  }
  const p = numero(programado);
  const deProgramado = p != null ? ` de ${pct(p)} programado` : "";
  return {
    real: r,
    programado: p,
    pista: enPista({ real: r, programado: p }, 1),
    clase: claseBarra(semaforo),
    texto: pct(r),
    accesible: `${que}: ${pct(r)}${deProgramado}`,
  };
}

/** Quién envió y quién resolvió un trimestre, en renglones cortos. */
function movimientos(t: BscEjercicioTrimestre): string[] {
  const lista: string[] = [];
  if (t.fechaEnvio || t.usuarioEnvia) {
    lista.push(`Envió ${t.usuarioEnvia || SIN_DATO}${conFecha(t.fechaEnvio)}`);
  }
  if (t.estatus === "AUTORIZADO") {
    if (t.origen === "IMPORTACION") {
      lista.push("Autorizado al cargar el FOR-011");
    } else if (t.usuarioResuelve || t.fechaResolucion) {
      lista.push(`Autorizó ${t.usuarioResuelve || SIN_DATO}${conFecha(t.fechaResolucion)}`);
    }
  } else if (t.estatus === "REGRESADO" && (t.usuarioResuelve || t.fechaResolucion)) {
    lista.push(`Regresó ${t.usuarioResuelve || SIN_DATO}${conFecha(t.fechaResolucion)}` +
      (t.motivo ? `: ${t.motivo}` : ""));
  }
  return lista;
}

/** ", 3 jul 2026" para seguir al nombre en los movimientos; vacío sin fecha. */
function conFecha(fecha: string | undefined): string {
  return fecha ? `, ${fechaCorta(fecha)}` : "";
}

/** La fecha límite del trimestre, solo mientras espera el envío del área. */
function textoLimite(t: BscEjercicioTrimestre | undefined): string {
  if (!t || !ABIERTOS.has(t.estatus ?? "PENDIENTE")) {
    return "";
  }
  return t.fechaLimiteEnvio ? `Límite ${fechaCorta(t.fechaLimiteEnvio)}` : "Sin fecha límite";
}

/** El color del tile por cuántos KPIs van fuera o en riesgo: fuera manda sobre en riesgo. */
function claseConteos(fuera: number, enRiesgo: number): string {
  if (fuera > 0) {
    return "ticket-kpi--total";
  }
  return enRiesgo > 0 ? "ticket-kpi--time" : "ticket-kpi--done";
}

/** El color del tile «Contra <año>» por la diferencia en puntos: sin dato, a favor (desde 0) o en contra. */
function claseDiferencia(delta: number | null): string {
  if (delta == null) {
    return "ticket-kpi--muted";
  }
  return delta >= 0 ? "ticket-kpi--done" : "ticket-kpi--total";
}

/** Cómo va este año contra el anterior, en palabras. */
function notaDiferencia(delta: number): string {
  if (delta > 0) {
    return "Este año va arriba";
  }
  return delta < 0 ? "Este año va abajo" : "Este año va igual";
}

/** El tono de esa nota: verde si va arriba, rojo si va abajo, sin tono si va igual. */
function tonoDiferencia(delta: number): TileBsc["notaClase"] {
  if (delta > 0) {
    return "is-exito";
  }
  return delta < 0 ? "is-peligro" : undefined;
}

/** La entrega en una o dos palabras, para el número grande del tile. */
function textoCortoEntrega(valor: string): string {
  switch (valor) {
    case "AUTORIZADA":
      return "Autorizada";
    case "ENVIADA":
      return "Enviada";
    case "PENDIENTE":
      return "Pendiente";
    case "VENCIDA":
      return "Vencida";
    case "SIN_DATOS":
      return "Sin datos";
    default:
      return "Sin planeación";
  }
}

/** Sin acentos y en minúsculas, para buscar. */
function normaliza(texto: string): string {
  return texto.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}
